/* eslint-disable ember/no-computed-properties-in-native-classes, ember/no-runloop, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { mapBy, sortBy } from '@linc-technologies/linced-common/utils/arrays';
import EventStatus from '@linc-technologies/linced-common/enums/bookings/event-status';
import { action, computed } from '@ember/object';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import { getTablePrintCss } from 'linced-app/utils/table';
import { hash } from '@linc-technologies/linced-common/utils/promise';
import {
  bookingTable,
  buildChipDataMap,
  teacherIdKey,
} from 'linced-app/utils/bookings';
import { compare } from '@ember/utils';

export default class IndexController extends Controller {
  @action
  setStatusChangeReason(value) {
    this.statusChangeReason = value;
  }

  @action
  setStatusFilter(value) {
    this.statusFilter = value;
  }

  // Services
  @service
  store;

  @service
  toaster;

  EventStatus = EventStatus;
  @tracked statusFilter = EventStatus.DRAFT;
  @tracked
  confirmStatusDialogOpen = false;
  @tracked
  statusEvent = null;
  @tracked
  newStatus = null;
  @tracked
  statusChangeInProgress = false;
  @tracked
  statusChangeReason = '';

  @computed('model.events.@each.status', 'statusFilter')
  get events() {
    return this.model.events.filter(
      (item) => item.status === this.statusFilter,
    );
  }

  resetEventHighlights() {
    this.model.events.forEach((event) => event.set('highlight', false));
  }

  @action
  statusChange(event, status) {
    this.statusEvent = event;
    this.newStatus = status;
    this.confirmStatusDialogOpen = true;
    this.statusChangeReason = '';
  }

  @action
  confirmStatusChange() {
    this.statusChangeInProgress = true;
    this.statusEvent.set('status', this.newStatus);
    if (
      this.newStatus === EventStatus.CANCELLED ||
      this.newStatus === EventStatus.CLOSED
    ) {
      this.statusEvent.set('statusChangeReason', this.statusChangeReason);
    }

    this.statusEvent
      .save()
      .then(() => {
        this.toaster.success('Event updated successfully!');
        if (this.newStatus !== EventStatus.CANCELLED) {
          this.statusFilter = this.newStatus;
          this.statusEvent.set('highlight', true);
          later(this, 'resetEventHighlights', 3000);
        }
      })
      .catch(() => {
        this.toaster.error('Error updating Event!');
      })
      .finally(() => {
        this.statusEvent = null;
        this.newStatus = null;
        this.confirmStatusDialogOpen = false;
        this.statusChangeInProgress = false;
      });
  }

  @action
  cancelStatusChange() {
    this.statusEvent = null;
    this.newStatus = null;
    this.confirmStatusDialogOpen = false;
  }

  @action
  print(event) {
    let printer = window.open(
      'about:blank',
      '_blank',
      'menubar=yes,location=no,resizable=yes,scrollbars=yes,status=no,height=700,width=1024',
    );

    // Ensure we've got all the event entities
    let eventId = event.id;
    hash({
      allocations: this.store.query('bookings/allocation', {
        eventId,
      }),
      bookings: this.store.query('bookings/booking', { eventId }),
      slots: this.store.query('bookings/slot', { eventId }),
    }).then((data) => {
      // Build up data - lifted from booking-manager component
      let chipDataMap = buildChipDataMap(
        data.allocations,
        data.slots,
        data.bookings,
      );

      let content = getTablePrintCss(false);

      // Do some sorting of teacher groups first
      let teacherGroupMappings = event.teacherGroupMappings
        .reduce((teacherGroups, mapping) => {
          teacherGroups.push(sortBy(mapping.get('teachers'), 'fullName'));
          return teacherGroups;
        }, [])
        .sort((a, b) => compare(a[0]?.fullName, b[0]?.fullName));

      let tablesStarted = false;
      teacherGroupMappings.forEach((teacherGroupMapping) => {
        let data = chipDataMap.get(
          teacherIdKey(teacherGroupMapping.map((item) => item.id)),
        );

        let table = bookingTable(data, event);

        if (table) {
          if (tablesStarted) {
            content += `<div style="page-break-before: always"></div>`;
          }
          content += `<h1>${event.get('name')}</h1>`;
          content += `<h2>${mapBy(teacherGroupMapping, 'fullName').join(
            ', ',
          )}</h2>`;
          content += table;
          tablesStarted = true;
        }
      });

      printer.document.body.innerHTML = content;

      later(
        this,
        (printer) => {
          printer.print();
          printer.close();
        },
        printer,
        500,
      );
    });
  }
}
